<template>
  <section class="top-inventories">
    <div class="discover-section mb40">
      <h2>{{ t("inventory.statistics.title") }}</h2>
      <p class="discover-section-subtitle">
        {{ t("inventory.statistics.subtitle") }}
      </p>
      <PagesInventoryStatisticsMarket :data="inventories?.data.inventoriesCommonInfo" />
    </div>

    <div class="inventories-section">
      <h2>{{ t("inventory.top_inventory") }}</h2>
      <div class="inventory-list">
        <div class="f gap16 inventory-filters">
          <MarketsDD
            v-model="market"
            :items="markets"
            label-key="market_name"
          />

          <!--          <UiSelect -->
          <!--              v-model="sortBy" -->
          <!--              :items="sortByOptions" -->
          <!--              label-key="label" -->
          <!--              translate -->
          <!--              value-key="value" -->
          <!--          > -->
          <!--            <template #top-in> -->
          <!--              {{ sortByOptions.find(el => el.value === sortBy)?.label }} -->
          <!--            </template> -->
          <!--            <template #default-in="{ item }"> -->
          <!--              <span class="f"> -->
          <!--                <span>{{ item.label }}</span> -->
          <!--              </span> -->
          <!--            </template> -->
          <!--          </UiSelect> -->
          <button
            :class="{ asc: sortBy === 'ASC' }"
            class="btn btn-sort"
            type="button"
            @click="
              sortBy
                = sortBy === 'DESC'
                  ? 'ASC'
                  : 'DESC';
            "
          >
            <span class="btn-sort__wrapper">
              <span class="btn-sort__line btn-sort__line_1" />
              <span class="btn-sort__line btn-sort__line_2" />
              <span class="btn-sort__line btn-sort__line_3" />
            </span>

            <span class="btn-sort__label">{{ $t("sort_by_price") }}</span>
          </button>
        </div>

        <div class="list">
          <PagesInventoryInventoriesListItem
            v-for="(item, i) in inventories?.data?.getUserInventories.data || []"
            :key="item?.steam64"
            :is-banned="isBanned"
            :is-top="sortBy === 'DESC' && isTop && i <= 2 && page === 1"
            :item="item as GetUserInventoriesUserOutput"
            :order="i + 1"
          />
        </div>
        <LazyPaginateV3
          v-if="maxPages > 1"
          v-model="page"
          :pages-length="maxPages"
          :total="totalItems"
          :total-items="limit * page > totalItems ? totalItems : limit * page"
        />
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import type {
  AvailableMarkets,
  GetUserInventoriesInputSort,
  GetUserInventoriesUserOutput,
} from '~/graphql/generated/schema-types';
import { PagesInventoryStatisticsMarket } from '#components';
import MarketsDD from '~/components/Pages/Inventories/MarketsDD.vue';

defineProps<{
  isTop?: boolean
  isBanned?: boolean
}>();

const { t } = useI18n();
const limit = ref(5);
const page = ref(1);
const market = ref();
const sortBy = ref<'ASC' | 'DESC'>('DESC');

defineExpose({
  market,
});

const { data: filters } = await useAsyncData(
  'getComparisonFilters',
  getComparisonFilters,
);

const { data: inventories, refresh } = useAsyncData(
  'getInventoriesTopList',
  async () => {
    try {
      if (!market.value) {
        return;
      }
      return await getInventories({
        page: page.value,
        limit: limit.value,
        sort: sortBy.value as GetUserInventoriesInputSort,
        market: market.value?.market_name,
      });
    } catch (error) {
      console.error('Ошибка при загрузке инвентаря:', error);
    }
  },
  { watch: [page] },
);

const markets = computed<AvailableMarkets[]>(
  () => (filters.value?.data.comparePageFilters.markets || []) as AvailableMarkets[],
);
const maxPages = computed<number>(
  () => inventories?.value?.data?.getUserInventories?.max_pages || 0,
);
const totalItems = computed<number>(
  () => inventories?.value?.data?.getUserInventories?.total_count || 0,
);

watchEffect(() => {
  if (markets.value.length > 0 && !market.value) {
    market.value = markets.value.find(el => el.market_name === 'waxpeer');
    refresh();
  }
});

watch([market, limit, sortBy], () => {
  page.value = 1;
  refresh();
});

watch(page, () => {
  refresh();
});
</script>

<style lang="scss" scoped>
@use "/assets/scss/mixins" as *;

h2 {
  font-size: 18px;
  font-weight: 800;
  margin-bottom: 24px;
  color: var(--surfaces-on-surface);
}

.btn-sort {
  min-height: 4.5rem;
  display: flex;
  justify-content: flex-start;
  gap: 6px;
  border: 1px solid #252930;

  &:hover {
    background: color-mix(in srgb, var(--surfaces-low-container) 96%, white);
  }

  &__wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    min-width: 18px;
    gap: 3px;
  }

  &__line {
    display: inline-block;
    height: 1.5px;
    border-radius: 2px;
    background: var(--accent-on-surface);
    transition: width 0.3s ease;

    &_1 {
      width: 18px;
    }

    &_2 {
      width: 14px;
    }

    &_3 {
      width: 10px;
    }
  }

  &__label {
    color: var(--surfaces-on-surface);
    font-size: 14px;
    font-weight: 600;
    line-height: normal;
    white-space: nowrap;
    @include ellipsis;
  }

  &.asc {
    .btn-sort__line {
      &_1 {
        width: 10px;
      }

      &_2 {
        width: 14px;
      }

      &_3 {
        width: 18px;
      }
    }
  }
}

.inventory-list {
  .inventory-filters {
    margin-bottom: 20px;

    .cselect {
      width: auto;
    }
  }

  .list {
    margin-bottom: 8px;
    color: var(--surfaces-on-surface);
  }
}

@media screen and (max-width: 600px) {
  .markets-dd {
    min-width: 50%;
    width: 50%;
    max-width: 50%;
  }
}
</style>
